/**
 * ПРОВЕРКА ОБНОВЛЕНИЯ ANDROID-ПРИЛОЖЕНИЯ (2026-10-05, задача Антона 17:09).
 *
 * ─── ЗАЧЕМ ──────────────────────────────────────────────────────────────────
 * В приложении НЕ БЫЛО проверки обновлений вообще: человек со старой сборкой
 * не узнавал о новой версии, пока сам не пойдёт смотреть. Серверная половина
 * уже живая: по HTTPS отдаётся манифест последней сборки. Этот модуль —
 * клиентская половина: сходить за манифестом, сравнить с установленной
 * версией и вернуть результат вызывающему. Диалог рисует App нашими
 * компонентами (UpdateDialog в этом же файле) — НЕ системный диалог и
 * НЕ alert(): внешний вид обязан совпадать с остальными диалогами
 * приложения (требование Антона дословно: «Весь внешний вид должен быть
 * единообразен»).
 *
 * ─── СЕРВЕРНЫЙ КОНТРАКТ (замерен живьём 2026-10-05, НЕ менять) ─────────────
 *   GET https://client.absolutelive.games/app/android/latest.json
 *   → 200 application/json:
 *     {
 *       "versionCode": 399871,                 // ЧИСЛО — сравнение только по нему
 *       "versionName": "1.0.261005.1631",      // строка для ПОКАЗА, не для сравнения:
 *                                              // "1.0.9" строкой БОЛЬШЕ "1.0.10"
 *       "url": "https://client.absolutelive.games/app/android/algsuccess-<name>-<code>.apk",
 *       "size": 7690575,                       // байты; показываем по-человечески (МБ)
 *       "sha256": "<hex>",
 *       "notes": "alg(success) Android update",
 *       "mandatory": false                     // true → кнопки «Позже» НЕТ
 *     }
 *
 * ─── ГДЕ РАБОТАЕТ, А ГДЕ МОЛЧИТ ────────────────────────────────────────────
 * Канал обновления существует ТОЛЬКО у Android-оболочки (Capacitor): манифест
 * описывает APK, и «свою версию» имеет смысл спрашивать у нативной сборки.
 * Поэтому модуль включается ровно при тех же условиях, что и мост шаринга
 * (src/ui/nativeShare.ts): глобал window.Capacitor + isNativePlatform() +
 * isPluginAvailable("App") — БЕЗ статического импорта @capacitor/app, чтобы
 * в браузере и на десктопе не выполнялась ни одна строка сетевого/нативного
 * кода. iOS/macOS НЕ трогаем: канала обновления у них нет (постановка).
 * Нет плагина, нет сети, кривой JSON, 404 — модуль молча возвращает null:
 * это ФОНОВАЯ проверка, она не имеет права ронять приложение или мешать.
 *
 * ─── КАК ЧАСТО ─────────────────────────────────────────────────────────────
 * Проверка запускается при старте приложения и при возврате из фона
 * (visibilitychange → visible), но НЕ чаще одного раза в 30 минут: отметка
 * времени последней попытки хранится в localStorage (`alg.update.checkedAt`),
 * так что перезапуск приложения не сбрасывает интервал, и сервер не долбится
 * на каждый фокус.
 *
 * ─── КТО ЧТО ДЕЛАЕТ ─────────────────────────────────────────────────────────
 *   checkForUpdate()  — чистая механика: Capacitor App.getInfo() → fetch
 *                       манифеста → числовое сравнение versionCode →
 *                       { versionName, size, url, mandatory } | null.
 *   shouldCheckNow()  / markChecked() — интервал и отметка времени.
 *   UpdateDialog      — модальное окно НАШИМИ Dialog/DialogButton
 *                       (src/ui/Dialog.tsx) и токенами темы: ни одного
 *                       hex-литерала. «Обновить» открывает url штатным
 *                       способом (window.open — тем же, что approveDeviceCode
 *                       и matrix.to-ссылки); на Android системный загрузчик
 *                       сам скачает APK. mandatory → без кнопки «Позже» и
 *                       без закрытия по Esc/клику по затемнению.
 *   updateCheckUi.ts  — вне-React клей для App: обёртки над интервалом,
 *                       открытие url, формат размера. Отдельно от UI, чтобы
 *                       App не знал про localStorage-ключи.
 */
import { useT, useTF } from "../i18n";
import { color, font, space } from "./theme";
import { Dialog, DialogButton } from "./Dialog";

/* ------------------------------------------------------------------------
 * МОСТ В CAPACITOR — тот же способ, что в nativeShare.ts: проверка
 * isPluginAvailable("App"), без прямого импорта пакета @capacitor/app.
 * В браузере/на десктопе capacitor() вернёт null, и весь модуль — no-op.
 * --------------------------------------------------------------------- */

/** Минимальная типизация глобала Capacitor (та же форма, что в nativeShare). */
interface CapacitorAppGlobal {
  isNativePlatform?: () => boolean;
  isPluginAvailable?: (name: string) => boolean;
  Plugins?: {
    App?: {
      /** getInfo() → { version: "1.0.261005.1631", build: "398525", … }.
          build — это versionCode СТРОКОЙ (контракт плагина). */
      getInfo: () => Promise<{ version: string; build: string }>;
    };
  };
}

function capacitor(): CapacitorAppGlobal | null {
  if (typeof window === "undefined") return null;
  const cap = (window as unknown as { Capacitor?: CapacitorAppGlobal }).Capacitor;
  if (!cap) return null;
  try {
    if (cap.isNativePlatform?.() !== true) return null;
  } catch {
    return null;
  }
  return cap;
}

/** Плагин App доступен в этой оболочке? false → модуль молча не работает. */
export function canCheckUpdate(): boolean {
  const cap = capacitor();
  if (!cap) return false;
  try {
    return cap.isPluginAvailable?.("App") === true && Boolean(cap.Plugins?.App?.getInfo);
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------------
 * СЕРВЕРНЫЙ МАНИФЕСТ
 * --------------------------------------------------------------------- */

const LATEST_URL = "https://client.absolutelive.games/app/android/latest.json";

/** Подмножество полей манифеста, которыми пользуемся (sha256/notes — нативной
    половине, вебу для диалога они не нужны). */
interface LatestManifest {
  versionCode: number;
  versionName: string;
  url: string;
  size: number;
  mandatory: boolean;
}

/** Проверка формы манифеста: кривой JSON/404/не-объект → null, молча. */
function parseManifest(raw: unknown): LatestManifest | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const versionCode = typeof o.versionCode === "number" && Number.isFinite(o.versionCode) ? o.versionCode : null;
  const versionName = typeof o.versionName === "string" && o.versionName ? o.versionName : null;
  const url = typeof o.url === "string" && /^https:\/\//.test(o.url) ? o.url : null;
  if (versionCode === null || versionName === null || url === null) return null;
  return {
    versionCode,
    versionName,
    url,
    size: typeof o.size === "number" && Number.isFinite(o.size) && o.size > 0 ? o.size : 0,
    mandatory: o.mandatory === true,
  };
}

/** Информация об обновлении, если оно есть. */
export interface AvailableUpdate {
  versionName: string;
  /** Байты; 0 = размер неизвестен, в диалоге не показываем. */
  size: number;
  url: string;
  mandatory: boolean;
}

/**
 * Сходить за манифестом и сравнить с установленной версией.
 *
 * Возвращает:
 *   AvailableUpdate — серверная versionCode ЧИСЛОВО больше нашей;
 *   null            — обновления нет ИЛИ любая тихая ошибка (нет моста,
 *                     нет сети, не-200, кривой JSON, наша версия не число).
 * Никогда не бросает исключений: фоновая проверка не имеет права уронить
 * приложение.
 */
export async function checkForUpdate(): Promise<AvailableUpdate | null> {
  try {
    const getInfo = capacitor()?.Plugins?.App?.getInfo;
    if (!getInfo) return null;
    const info = await getInfo();
    // build приходит СТРОКОЙ ("398525"). Не число → сравнивать нечего.
    const ownCode = Number.parseInt(info.build, 10);
    if (!Number.isFinite(ownCode)) return null;

    const res = await fetch(LATEST_URL, { cache: "no-store" });
    if (!res.ok) return null;
    const manifest = parseManifest(await res.json().catch(() => null));
    if (!manifest) return null;

    // Сравнение ЧИСЛОВОЕ по versionCode: versionName строкой сравнивается
    // неверно ("1.0.9" > "1.0.10"). Строго больше → есть обновление.
    if (manifest.versionCode <= ownCode) return null;
    return {
      versionName: manifest.versionName,
      size: manifest.size,
      url: manifest.url,
      mandatory: manifest.mandatory,
    };
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------------
 * ДИАЛОГ — НАШИ компоненты и НАШИ токены темы (src/ui/theme.ts), ровно как
 * остальные модальные окна приложения. Ни системного диалога, ни alert(),
 * ни одного hex-литерала: тёмная тема работает через те же var(--alg-*).
 * --------------------------------------------------------------------- */

export function UpdateDialog({
  update,
  sizeLabel,
  onUpdate,
  onLater,
}: {
  update: AvailableUpdate;
  /** Размер файла по-человечески ("7,3 МБ") — считается вне компонента,
      потому что формат зависит от локали. */
  sizeLabel: string;
  onUpdate: () => void;
  onLater: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  // mandatory: кнопки «Позже» НЕТ (постановка), и окно не закрывается
  // по Esc/клику по затемнению — иначе «обязательное» обновление уходило
  // бы одним касанием. Закрытие у Dialog подменить нельзя, поэтому onClose
  // уходит в пустышку.
  const noop = () => {};
  return (
    <Dialog
      title={t("update.title")}
      onClose={update.mandatory ? noop : onLater}
      footer={
        <>
          {!update.mandatory && (
            <DialogButton kind="plain" onClick={onLater} testId="update-later">
              {t("update.later")}
            </DialogButton>
          )}
          <DialogButton kind="primary" onClick={onUpdate} testId="update-now">
            {t("update.now")}
          </DialogButton>
        </>
      }
    >
      <div style={{ fontSize: font.body, color: color.text, lineHeight: 1.4 }}>
        {update.size > 0
          ? tfmt("update.bodySize", { version: update.versionName, size: sizeLabel })
          : tfmt("update.body", { version: update.versionName })}
      </div>
      {update.mandatory && (
        <div style={{ fontSize: font.small, color: color.textDim, marginTop: -space.sm }}>
          {t("update.mandatoryNote")}
        </div>
      )}
    </Dialog>
  );
}
